import React from 'react';
import { Switch, Route, useLocation, useHistory } from 'react-router-dom';
import { Container } from '@material-ui/core';
import { Redirect } from 'react-router';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import MuiTheme from './theme';

import AuthenticationRoute from './containers/AuthenticationRoute';
import EfesFooter from './components/AppLayout/Footer';
import { BackStackProvider } from './providers/BackStackProvider';
import useAnalytics from './hooks/useAnalytics';
import useIsHomePage from './hooks/useIsHomePage';
import useBrowserBackStack from './hooks/useBrowserBackStack';
import { authRouteTree, getFlattenRoutes } from './helpers/menuTree/menuTree';
import { CONTAINER_WIDTH, HOME_PAGE_CONTAINER_WIDTH } from './constants/uiConstants';
import {
  ACCIDENT_COMPENSATION_PATH,
  CASCO_COMPENSATION_PATH,
  CLAIM_STATUS_PATH,
  CONTRACTS_VALIDATION_PATH,
  MEDICAL_COMPENSATION_PATH,
  CONTRACT_SUBSCRIPTION_PATH,
  PROPERTY_COMPENSATION_PATH,
  RECURRING_PATH,
  TRAVEL_CALCULATOR_PATH,
  TRAVEL_COMPENSATION_PATH,
  TRAVEL_CONTRACT_SIGN_PATH,
  ONCOCARE_CONTRACT_SIGN_PATH,
  ONCOCARE_CALCULATOR_PATH,
  ROADCARE_CALCULATION_PATH,
  ROADCARE_CONTRACT_SIGN_PATH,
} from './constants/routes';
import { useChangeAppLanguage } from './hooks/useChangeAppLanguage';
import { useChangeURLLanguageSearchParams } from './hooks/useChangeURLLanguageSearchParams';

/// Pages
import HomePage from './pages/HomePage';
import OncoCareCalculatorPage from './pages/OncoCareCalculatorPage';
import TravelCalculatorPage from './pages/TravelCalculatorPage';
import CheckContractPolicyPage from './pages/CheckContractPolicyPage';
import RoadCareCalculatorPage from './pages/RoadCareCalculatorPage';
import TravelContractConfirmation from './components/Confirmation/TravelContractConfirmation';
import OncoCareContractConfirmation from './components/Confirmation/OncoCareContractConfirmation';
import AttachCardToPolicyConfirmation from './components/Confirmation/AttachCardToPolicyConfirmation';
import RecurringPaymentConfirmation from './components/Confirmation/RecurringPaymentConfirmation';
import ClaimStatusCheck from './components/ClaimStatusCheck';

/// Containers
import ResponsiveContainer from './components/AppLayout/ResponsiveContainer';
import TravelContractContainer from './containers/TravelContractContainer';
import OncoCareContractContainer from './containers/OncoCareContractContainer';
import AccidentCompensationContainer from './containers/AccidentCompensationContainer';
import PropertyCompensationContainer from './containers/PropertyCompensationContainer';
import AttachCardToPolicyContainer from './containers/AttachCardToPolicyContainer';
import MakeRecurringPaymentContainer from './containers/MakeRecurringPaymentContainer';
import TravelCompensationContainer from './containers/TravelCompensationContainer';
import CascoCompensationContainer from './containers/CascoCompensationContainer';
import MedicalCompensationContainer from './containers/MedicalCompensationContainer';
import RoadCareContractContainer from './containers/RoadCareContractContainer';
import RoadCareContractConfirmation from './components/Confirmation/RoadCareContractConfirmation';

const flattenAuthRoutes = getFlattenRoutes(authRouteTree);

const App = () => {
  useAnalytics();
  const { i18n } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);

  const isHomePage = useIsHomePage();
  const backStack = useBrowserBackStack();

  const language = urlSearchParams.get('lang');
  useChangeAppLanguage(language);
  useChangeURLLanguageSearchParams(urlSearchParams, i18n, history);

  // TODO: A new way of handling admin access route
  // const user = useSelector((store) => store.userReducer.user);
  // const roles = user && user.roles && user.roles.map((role) => role.name);
  // const isAdmin = roles && roles.includes(USER_ROLES.ADMIN);
  // const flattenAuthRoutesWithAccess = flattenAuthRoutes.filter((route) => (
  //   !(route.isAdminRoute && !isAdmin)
  // ));

  return (
    <ThemeProvider theme={MuiTheme}>
      <BackStackProvider backStack={backStack}>
        <ResponsiveContainer>
          <ToastContainer
            position="top-center"
            autoClose={false}
            hideProgressBar
            closeOnClick
            draggable
            closeButton={false}
            className="toast-notification"
          />
          <Container
            style={{
              padding: 0,
              maxWidth: isHomePage ? HOME_PAGE_CONTAINER_WIDTH : CONTAINER_WIDTH,
            }}
          >
            <Switch>
              {/* ============== START OF auth insensitive routes ============== */}
              <Route exact path="/" component={HomePage} />

              <Route exact path={CLAIM_STATUS_PATH} component={ClaimStatusCheck} />
              <Route path={TRAVEL_CALCULATOR_PATH} component={TravelCalculatorPage} />
              <Route
                exact
                path={`${RECURRING_PATH}/confirmation`}
                component={RecurringPaymentConfirmation}
              />
              <Route path={RECURRING_PATH} component={MakeRecurringPaymentContainer} />
              <Route
                exact
                path={`${CONTRACT_SUBSCRIPTION_PATH}/attach-card/confirmation`}
                component={AttachCardToPolicyConfirmation}
              />
              <Route path={CONTRACT_SUBSCRIPTION_PATH} component={AttachCardToPolicyContainer} />
              <Route path={CONTRACTS_VALIDATION_PATH} component={CheckContractPolicyPage} />
              <Route
                exact
                path={`${TRAVEL_CONTRACT_SIGN_PATH}/confirmation`}
                component={TravelContractConfirmation}
              />
              <Route path={TRAVEL_CONTRACT_SIGN_PATH} component={TravelContractContainer} />
              <Route path={MEDICAL_COMPENSATION_PATH} component={MedicalCompensationContainer} />
              <Route path={CASCO_COMPENSATION_PATH} component={CascoCompensationContainer} />
              <Route path={TRAVEL_COMPENSATION_PATH} component={TravelCompensationContainer} />
              <Route path={ACCIDENT_COMPENSATION_PATH} component={AccidentCompensationContainer} />
              <Route path={PROPERTY_COMPENSATION_PATH} component={PropertyCompensationContainer} />
              <Route
                exact
                path={`${ONCOCARE_CONTRACT_SIGN_PATH}/confirmation`}
                component={OncoCareContractConfirmation}
              />
              <Route path={ONCOCARE_CONTRACT_SIGN_PATH} component={OncoCareContractContainer} />
              <Route path={ONCOCARE_CALCULATOR_PATH} component={OncoCareCalculatorPage} />

              <Route
                exact
                path={`${ROADCARE_CONTRACT_SIGN_PATH}/confirmation`}
                component={RoadCareContractConfirmation}
              />
              <Route path={ROADCARE_CONTRACT_SIGN_PATH} component={RoadCareContractContainer} />
              <Route path={ROADCARE_CALCULATION_PATH} component={RoadCareCalculatorPage} />
              <AuthenticationRoute
                path="/contract"
                withAuth
                redirectOnFailure="/"
                render={() => (
                  <Switch>
                    {/* <Route path="/contract/cmtpl/:id" component={CMTPLInsurance} /> */}
                  </Switch>
                )}
              />

              {/* ============== END OF auth insensitive routes ================ */}

              {/* ============== START OF non auth routes ====================== */}
              {/* @TODO will be open in the latter */}
              {/* <AuthenticationRoute */}
              {/*  withAuth={false} */}
              {/*  path="/sign-up" */}
              {/*  redirectOnFailure="/" */}
              {/*  component={UserRegistrationForm} */}
              {/* /> */}

              {/* ============== END OF non auth routes ========================= */}

              {/* ============== START OF auth routes =========================== */}
              <AuthenticationRoute
                path="/"
                withAuth
                redirectOnFailure="/"
                render={() => (
                  <Switch>
                    {flattenAuthRoutes.map((route) => (
                      <Route
                        key={route.path}
                        component={route.component}
                        path={route.path}
                        exact={route.isExact}
                      />
                    ))}
                    <Redirect to="/" />
                  </Switch>
                )}
              />

              {/* ============== END OF auth routes ============================= */}

              {/* ============== DEFAULT REDIRECT ROUTE */}
            </Switch>
          </Container>
        </ResponsiveContainer>
      </BackStackProvider>
      <EfesFooter />
    </ThemeProvider>
  );
};
export default App;
